import { cn } from '@/lib/utils';
import { SortTypeEnum } from '@/request/request.enum';
import tokenApiService from '@/request/token/token.api.service';
import { IPumpToken } from '@/request/token/token.response.interface';
import BigNumber from 'bignumber.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TokenHead from './components/TokenHead';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import TokenList from './components/TokenList';
import useSubscribePumpCreate from './hook/useSubscribePumpCreate';
import { useAppStore } from '@/store';
import { useBoomWallet } from '@boomeme/wallet-sdk';
type TabType = 'newBorn' | 'launching' | 'launched';

export interface SortConfig {
  key: 'top10' | 'dev' | 'volume' | 'marketCap' | 'progress' | 'createTime' | 'createPoolTime';
  direction: SortTypeEnum;
}

export default () => {
  const { fwClass } = useAppStore();
  const { t } = useTranslation();
  const { chain } = useBoomWallet();
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const { subscribe } = useAppEventBusContext();
  useSubscribePumpCreate();

  const [newBornTokens, setNewBornTokens] = useState<IPumpToken[]>([]);
  const [launchingTokens, setLaunchingTokens] = useState<IPumpToken[]>([]);
  const [launchedTokens, setLaunchedTokens] = useState<IPumpToken[]>([]);

  const [sortConfigs, setSortConfigs] = useState<Record<TabType, SortConfig>>({
    newBorn: { key: 'createTime', direction: SortTypeEnum.DESC },
    launching: { key: 'progress', direction: SortTypeEnum.DESC },
    launched: { key: 'createPoolTime', direction: SortTypeEnum.DESC },
  });

  const tabs = [t('Token.newCreations'), t('Token.aboutToGraduate'), t('Token.graduated')];
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [newBornRes, launchingRes, launchedRes] = await Promise.all([
          tokenApiService.getPumpNewBorn(),
          tokenApiService.getPumpLaunching(),
          tokenApiService.getPumpLaunched(),
        ]);

        if (newBornRes.code === 0) setNewBornTokens(newBornRes.data.map((item) => ({ ...item, createPoolTime: '0' })));
        if (launchingRes.code === 0) setLaunchingTokens(launchingRes.data.map((item) => ({ ...item, createPoolTime: '0' })));
        if (launchedRes.code === 0) setLaunchedTokens(launchedRes.data);
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAllData();
  }, [chain]);

  const currentTab = useMemo<TabType>(() => {
    return ['newBorn', 'launching', 'launched'][activeIndex] as TabType;
  }, [activeIndex]);

  const currentSortConfig = useMemo(() => {
    return sortConfigs[currentTab];
  }, [currentTab, sortConfigs]);

  const compareValues = (a: IPumpToken, b: IPumpToken, key: SortConfig['key']): number => {
    const getValue = (item: IPumpToken): BigNumber => {
      switch (key) {
        case 'top10':
          return BigNumber(item.top10);
        case 'dev':
          return BigNumber(item.dev);
        case 'volume':
          return BigNumber(item.volume);
        case 'marketCap':
          return BigNumber(item.marketCap);
        case 'progress':
          return BigNumber(item.progress);
        default:
          return new BigNumber(0);
      }
    };

    const valueA = getValue(a);
    const valueB = getValue(b);
    return currentSortConfig.direction === SortTypeEnum.ASC ? valueA.minus(valueB).toNumber() : valueB.minus(valueA).toNumber();
  };

  const sortedTokens = useMemo(() => {
    return {
      newBorn: [...newBornTokens].sort((a, b) => compareValues(a, b, sortConfigs.newBorn.key)),
      launching: [...launchingTokens].sort((a, b) => compareValues(a, b, sortConfigs.launching.key)),
      launched: [...launchedTokens].sort((a, b) => compareValues(a, b, sortConfigs.launched.key)),
    };
  }, [newBornTokens, launchingTokens, launchedTokens, sortConfigs]);

  const handleSort = (key: SortConfig['key']) => {
    setSortConfigs((prev) => ({
      ...prev,
      [currentTab]: {
        key,
        direction: prev[currentTab].key === key && prev[currentTab].direction === SortTypeEnum.DESC ? SortTypeEnum.ASC : SortTypeEnum.DESC,
      },
    }));
  };

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Pump_Create, (data: IPumpToken) => {
      setNewBornTokens((prev) => [data, ...prev]);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== activeIndex) {
      swiperRef.current.slideTo(activeIndex);
    }
  }, [activeIndex]);

  return (
    <div>
      <div className="my-[12px] flex gap-x-[8px]">
        {tabs.map((item, index) => {
          return (
            <div
              key={item}
              className={cn(
                'px-[9px] py-[6px] h-[30px] text-[12px] text-secondaryTextColor border-[1px] border-borderColor text-nowrap flex items-center rounded-[6px]',
                fwClass,
                index == activeIndex && 'border-themeColor text-primaryTextColor',
              )}
              onClick={() => setActiveIndex(index)}
            >
              {item}
            </div>
          );
        })}
      </div>
      <TokenHead
        getSortStatus={(key) => (currentSortConfig.key === key ? currentSortConfig.direction : SortTypeEnum.DEFAULT)}
        handleSort={handleSort}
      />
      <div>
        <Swiper
          className="w-full h-full"
          initialSlide={activeIndex}
          onSlideChange={(e) => setActiveIndex(e.activeIndex)}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
        >
          <SwiperSlide>
            <TokenList activeIndex={activeIndex} sortedTokens={sortedTokens.newBorn} loading={initialLoading} />
          </SwiperSlide>
          <SwiperSlide>
            <TokenList activeIndex={activeIndex} sortedTokens={sortedTokens.launching} loading={initialLoading} />
          </SwiperSlide>
          <SwiperSlide>
            <TokenList activeIndex={activeIndex} sortedTokens={sortedTokens.launched} loading={initialLoading} />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  );
};
